import React from 'react';
import { Box, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { CreditPlanFields } from '../../types/clientAccount';

// Desglose «dos bolsas» del saldo del cliente en TRES cifras claras y
// etiquetadas: Plan restante (bolsa mensual que caduca este ciclo), Extra
// restante (bolsa puntual que no caduca) y Total disponible. Antes el portal
// enseñaba solo el total, y el cliente no sabía cuánto de su saldo iba a
// caducar. Los números salen de summary_numbers del backend (plan_remaining /
// extra_balance / available_total) — NO se re-derivan aquí.
//
// Compartido entre «Mi cuenta» y la página de historial para que las dos
// cuenten exactamente lo mismo. Vive en el portal cliente (no toca el
// CreditsSummary del admin).

const formatCredits = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '0.00';
  const num = Number(value);
  return Number.isNaN(num) ? String(value) : num.toFixed(2);
};

// 'YYYY-MM-DD' anclado a medianoche LOCAL (sin la T, JS lo parsea como UTC y en
// husos negativos pinta el día anterior).
const formatRenewDate = (raw: string): string | null => {
  const normalized = raw.includes('T') ? raw : `${raw}T00:00:00`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

// Fecha Y HORA de un asiento del ledger. El created_at del backend es UTC naive:
// se le añade la Z para que el navegador lo pase a la hora local, y se pinta
// completo (fecha + hora) — antes solo salía la fecha y dos movimientos del
// mismo día no se distinguían.
export const formatCreditDateTime = (iso: string): string => {
  const utc = iso.endsWith('Z') ? iso : `${iso}Z`;
  const d = new Date(utc);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// Etiqueta legible de CADA kind del ledger. Sin esto salían crudos («plan_grant»,
// «plan_expiry»). Se centraliza para que «Mi cuenta» y el historial coincidan.
export const creditKindLabel = (kind: string, t: TFunction): string => {
  switch (kind) {
    case 'topup':
      return t('customer.account.creditKind.topup', 'Recarga');
    case 'consumption':
      return t('customer.account.creditKind.consumption', 'Consumo');
    case 'adjustment':
      return t('customer.account.creditKind.adjustment', 'Ajuste');
    case 'plan_grant':
      return t('customer.account.creditKind.planGrant', 'Alta de plan');
    case 'plan_expiry':
      return t('customer.account.creditKind.planExpiry', 'Caducidad de plan');
    case 'storage':
      return t('customer.account.creditKind.storage', 'Almacenamiento');
    default:
      return kind;
  }
};

const toNum = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

interface CreditFigureProps {
  label: string;
  value: number | string | null | undefined;
  emphasis?: boolean;
  caption?: string | null;
}

const CreditFigure: React.FC<CreditFigureProps> = ({ label, value, emphasis, caption }) => (
  <Box sx={{ flex: '1 1 120px', minWidth: 120 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant={emphasis ? 'h4' : 'h5'}
      fontWeight={700}
      color={emphasis ? 'primary.main' : 'text.primary'}
    >
      {formatCredits(value)}
    </Typography>
    {caption ? (
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    ) : null}
  </Box>
);

interface CreditBreakdownProps {
  // Cualquier objeto con los campos «dos bolsas» (CustomerAccountSummary,
  // CustomerCreditsResponse, credit_summary…).
  data?: CreditPlanFields | null;
  // Saldo simple actual — fallback cuando el backend aún no habla «dos bolsas».
  balance?: number | string | null;
}

const CreditBreakdown: React.FC<CreditBreakdownProps> = ({ data, balance }) => {
  const { t } = useTranslation();

  const planRemaining = toNum(data?.plan_remaining);
  const extraBalance = toNum(data?.extra_balance);
  const availableTotal = toNum(data?.available_total);

  // Señal de que el backend ya emite el modelo «dos bolsas». Sin él, se pinta
  // solo el saldo simple (comportamiento previo), sin romper.
  const hasTwoBags = availableTotal !== null;
  if (!hasTwoBags) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('creditsSummary.availableTotal', 'Disponible total')}
        </Typography>
        <Typography variant="h4" fontWeight={700} color="primary.main">
          {formatCredits(balance)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('creditsSummary.creditsUnit', 'créditos')}
        </Typography>
      </Box>
    );
  }

  const renewDate = data?.plan_renews_at ? formatRenewDate(data.plan_renews_at) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-end' }}>
        <CreditFigure
          label={t('customer.account.breakdown.plan', 'Plan (caduca este ciclo)')}
          value={planRemaining ?? 0}
          caption={
            renewDate
              ? t('creditsSummary.renewsAt', 'se renueva el {{date}}', { date: renewDate })
              : null
          }
        />
        <CreditFigure
          label={t('customer.account.breakdown.extra', 'Extra (no caduca)')}
          value={extraBalance ?? 0}
        />
        <CreditFigure
          label={t('customer.account.breakdown.total', 'Total disponible')}
          value={availableTotal}
          emphasis
          caption={t('creditsSummary.creditsUnit', 'créditos')}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        {t(
          'customer.account.breakdown.hint',
          'Se gastan primero los créditos del plan; lo que sobre del plan caduca al cerrar el ciclo.'
        )}
      </Typography>
    </Box>
  );
};

export default CreditBreakdown;
